import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LineChart,
  Line,
} from "recharts";

import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
  SKIN_THEMES,
} from "../layouts/constants";

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");

  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }

  return `${base}${path}`;
}

function formatValue(value) {
  if (value === null || value === undefined || value === "") return "-";

  if (
    typeof value === "string" &&
    (value.includes("T") || value.includes("Z")) &&
    !Number.isNaN(new Date(value).getTime())
  ) {
    return new Date(value).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
    });
  }

  return String(value);
}

function normalizeText(value) {
  return String(value || "").toLowerCase();
}

function getRowDate(row) {
  return (
    row.scanning_time ||
    row.created_at ||
    row.start_time ||
    row.end_time ||
    row.updated_at ||
    null
  );
}

function groupCount(rows, key) {
  const map = {};

  rows.forEach((row) => {
    const value = row[key] || "Unknown";
    map[value] = (map[value] || 0) + 1;
  });

  return Object.entries(map)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function groupByDate(rows) {
  const map = {};

  rows.forEach((row) => {
    const rawDate = getRowDate(row);
    if (!rawDate) return;

    const date = new Date(rawDate).toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
    });

    map[date] = (map[date] || 0) + 1;
  });

  return Object.entries(map).map(([date, count]) => ({
    date,
    count,
  }));
}

function averageNumber(rows, key) {
  const values = rows
    .map((row) => Number(row[key]))
    .filter((value) => !Number.isNaN(value) && value > 0);

  if (!values.length) return 0;

  return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
}

function sumNumber(rows, key) {
  return rows.reduce((sum, row) => {
    const value = Number(row[key]);
    return sum + (Number.isNaN(value) ? 0 : value);
  }, 0);
}

function getReportSummary(reportType, rows) {
  const total = rows.length;

  if (reportType === "attendance") {
    return [
      { label: "Total Guards", value: total },
      {
        label: "Active Guards",
        value: rows.filter((r) => normalizeText(r.active_status) === "active").length,
      },
      {
        label: "Approved",
        value: rows.filter((r) => normalizeText(r.approval_status).includes("approve")).length,
      },
      {
        label: "Designations",
        value: new Set(rows.map((r) => r.fld_designation).filter(Boolean)).size,
      },
    ];
  }

  if (reportType === "qr") {
    return [
      { label: "Total QR Scans", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      { label: "Routes Covered", value: new Set(rows.map((r) => r.route_id)).size },
      { label: "Checkpoints", value: new Set(rows.map((r) => r.checkpoint_id)).size },
    ];
  }

  if (reportType === "incidents") {
    return [
      { label: "Total Incidents", value: total },
      {
        label: "Open Incidents",
        value: rows.filter((r) => normalizeText(r.status) === "open").length,
      },
      {
        label: "Closed Incidents",
        value: rows.filter((r) => normalizeText(r.status) === "closed").length,
      },
      {
        label: "Avg Resolution Hours",
        value: averageNumber(rows, "resolution_hours"),
      },
    ];
  }

  if (reportType === "alerts") {
    return [
      { label: "Total Alerts", value: total },
      {
        label: "Acknowledged",
        value: rows.filter((r) => r.acknowledged_at).length,
      },
      {
        label: "Pending",
        value: rows.filter((r) => !r.acknowledged_at).length,
      },
      {
        label: "Avg Ack Minutes",
        value: averageNumber(rows, "acknowledgement_minutes"),
      },
    ];
  }

  if (reportType === "route") {
    return [
      { label: "Total Allocations", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      { label: "Routes", value: new Set(rows.map((r) => r.route_id)).size },
      {
        label: "Avg Shift Minutes",
        value: averageNumber(rows, "shift_duration_minutes"),
      },
    ];
  }

  if (reportType === "escort") {
    return [
      { label: "Total Escorts", value: total },
      { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
      {
        label: "Total Distance KM",
        value: sumNumber(rows, "distance_km").toFixed(2),
      },
      {
        label: "Avg Duration Minutes",
        value: averageNumber(rows, "duration_minutes"),
      },
    ];
  }

  return [
    { label: "Total Records", value: total },
    { label: "Unique Guards", value: new Set(rows.map((r) => r.guard_id)).size },
    { label: "Report Type", value: reportType.toUpperCase() },
    { label: "Rows", value: total },
  ];
}

const REPORT_TYPES = [
  {
    key: "attendance",
    label: "Attendance",
    endpoint: "/api/sentinel_report/attendance",
  },
  {
    key: "qr",
    label: "QR Scans",
    endpoint: "/api/sentinel_report/qr-scans",
  },
  {
    key: "incidents",
    label: "Incidents",
    endpoint: "/api/sentinel_report/incidents",
  },
  {
    key: "alerts",
    label: "Alerts",
    endpoint: "/api/sentinel_report/alerts",
  },
  {
    key: "route",
    label: "Route Performance",
    endpoint: "/api/sentinel_report/route-performance",
  },
  {
    key: "escort",
    label: "Escort Performance",
    endpoint: "/api/sentinel_report/escort-performance",
  },
];

const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
  "#ca8a04",
  "#475569",
];

export default function SentinelReports() {
  const navigate = useNavigate();
  const skin = useSkin();

  const [guards, setGuards] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [reportType, setReportType] = useState("attendance");
  const [guardId, setGuardId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const selectedReport = useMemo(() => {
    return REPORT_TYPES.find((r) => r.key === reportType) || REPORT_TYPES[0];
  }, [reportType]);

  const summaryCards = useMemo(() => {
    return getReportSummary(reportType, rows);
  }, [reportType, rows]);

  const trendData = useMemo(() => {
    return groupByDate(rows);
  }, [rows]);

  const guardChartData = useMemo(() => {
    return groupCount(rows, "guard_name").slice(0, 10);
  }, [rows]);

  const severityChartData = useMemo(() => {
    return groupCount(rows, "severity");
  }, [rows]);

  const statusChartData = useMemo(() => {
    if (reportType === "alerts") return groupCount(rows, "alert_status");
    return groupCount(rows, "status");
  }, [rows, reportType]);

  const routeChartData = useMemo(() => {
    return groupCount(rows, "route_name").slice(0, 10);
  }, [rows]);

  const fetchGuards = useCallback(async () => {
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const res = await fetch(api("/api/sentinel_report/guards"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to fetch guards");
      }

      setGuards(json.data || []);
    } catch (err) {
      console.error("Guard fetch error:", err);
      setGuards([]);
    }
  }, []);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      setPageError("");

      const token = localStorage.getItem("token") || localStorage.getItem("authToken");

      const params = new URLSearchParams();

      if (guardId) params.set("guardId", guardId);
      if (fromDate) params.set("fromDate", fromDate);
      if (toDate) params.set("toDate", toDate);

      const url = api(`${selectedReport.endpoint}?${params.toString()}`);

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const json = await res.json();

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to fetch report");
      }

      setRows(json.data || []);
    } catch (err) {
      console.error("Report fetch error:", err);
      setRows([]);
      setPageError(err.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  }, [selectedReport, guardId, fromDate, toDate]);

  useEffect(() => {
    fetchGuards();
  }, [fetchGuards]);

  useEffect(() => {
    fetchReport();
  }, [reportType, fetchReport]);

  function handleExportCsv() {
    if (!rows.length) {
      alert("No data to export");
      return;
    }

    const headers = Object.keys(rows[0]);

    const csv = [
      headers.join(","),
      ...rows.map((row) =>
        headers
          .map((key) => {
            const value = row[key] === null || row[key] === undefined ? "" : String(row[key]);
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `${selectedReport.key}-report.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }

  return (
    <Layout activePage="reports" activeMenu="reports">
      <div
        className="min-h-screen p-4 md:p-6"
        style={{
          fontFamily: FONT_UI,
          background: skin.bg || "#f8fafc",
        }}
      >
        <div className="mb-5 flex items-center gap-3 flex-wrap">
          <div>
            <h1
              className="text-xl md:text-2xl font-bold"
              style={{
                fontFamily: FONT_HEADING,
                color: skin.text || "#111827",
              }}
            >
              Sentinel Reports
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Customer-friendly reports with KPI cards, charts, trends and detailed data.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="ml-auto px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
          >
            Dashboard
          </button>
        </div>

        {pageError ? (
          <div className="mb-5 px-4 py-3 rounded-xl text-sm font-medium border bg-red-50 border-red-200 text-red-700">
            {pageError}
          </div>
        ) : null}

        <div className="bg-white border border-slate-100 rounded-2xl p-4 mb-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-xs font-bold text-slate-600">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="mt-1 w-full border border-slate-200 px-3 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100"
              >
                {REPORT_TYPES.map((r) => (
                  <option key={r.key} value={r.key}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-600">
                Guard
              </label>
              <select
                value={guardId}
                onChange={(e) => setGuardId(e.target.value)}
                className="mt-1 w-full border border-slate-200 px-3 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100"
              >
                <option value="">All Guards</option>

                {guards.map((g) => {
                  const id = g.fld_memberId || g.member_id || g.guard_id;
                  const name = g.fld_name || g.fullName || g.guard_name || g.fld_username;

                  return (
                    <option key={id} value={id}>
                      {name} ({id})
                    </option>
                  );
                })}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-600">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="mt-1 w-full border border-slate-200 px-3 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div>
              <label className="text-xs font-bold text-slate-600">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="mt-1 w-full border border-slate-200 px-3 py-2 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-100"
              />
            </div>

            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={fetchReport}
                className="w-full rounded-xl text-white text-sm font-bold px-4 py-2"
                style={{
                  background: skin.gradient || "#2563eb",
                }}
              >
                Search
              </button>

              <button
                type="button"
                onClick={handleExportCsv}
                className="w-full rounded-xl border border-slate-200 bg-white text-blue-600 text-sm font-bold px-4 py-2 hover:bg-slate-50"
              >
                Export
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
          {summaryCards.map((card) => (
            <div
              key={card.label}
              className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm"
            >
              <p className="text-xs font-bold text-slate-500 uppercase">
                {card.label}
              </p>
              <h2 className="text-2xl font-black text-slate-900 mt-2">
                {card.value}
              </h2>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-slate-900 mb-1">Daily Report Trend</h3>
            <p className="text-xs text-slate-500 mb-4">
              Shows how report activity changes day by day.
            </p>

            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={3} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
            <h3 className="font-bold text-slate-900 mb-1">Top Guards by Activity</h3>
            <p className="text-xs text-slate-500 mb-4">
              Helps customers understand which guards have the highest activity.
            </p>

            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={guardChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#2563eb" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {(reportType === "alerts" || reportType === "incidents") && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-1">Severity Breakdown</h3>
              <p className="text-xs text-slate-500 mb-4">
                Shows risk level distribution for customer review.
              </p>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={severityChartData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                      label
                    >
                      {severityChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm">
              <h3 className="font-bold text-slate-900 mb-1">Status Breakdown</h3>
              <p className="text-xs text-slate-500 mb-4">
                Shows open, closed, pending, or acknowledged status.
              </p>

              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusChartData}
                      dataKey="value"
                      nameKey="name"
                      outerRadius={95}
                      label
                    >
                      {statusChartData.map((entry, index) => (
                        <Cell
                          key={entry.name}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {(reportType === "qr" || reportType === "route") && (
          <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-sm mb-5">
            <h3 className="font-bold text-slate-900 mb-1">Route Activity</h3>
            <p className="text-xs text-slate-500 mb-4">
              Shows which routes have the highest report activity.
            </p>

            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={routeChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" hide />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#16a34a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <b className="text-slate-900">{selectedReport.label} Detailed Report</b>
              <p className="text-xs text-slate-500 mt-1">
                Complete data table for customer audit and export.
              </p>
            </div>

            <span className="text-sm text-slate-500">
              {rows.length} records
            </span>
          </div>

          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-6 text-center text-slate-500">
                Loading report...
              </div>
            ) : rows.length === 0 ? (
              <div className="p-6 text-center text-slate-500">
                No data found
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    {Object.keys(rows[0]).map((key) => (
                      <th
                        key={key}
                        className="px-3 py-3 text-left font-bold text-slate-700 whitespace-nowrap"
                      >
                        {key.replaceAll("_", " ").toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={index}
                      className="border-t border-slate-100 hover:bg-slate-50"
                    >
                      {Object.keys(rows[0]).map((key) => (
                        <td
                          key={key}
                          className="px-3 py-3 text-slate-700 whitespace-nowrap"
                        >
                          {formatValue(row[key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}